<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { features, useFeatureFlag, useUiStateStore } from '@peanuds/app-shell';
import { ControlSpendings } from '@peanuds/control-spending';
import { QuickTransfer } from '@peanuds/money';
import { IbanSwiper } from '@peanuds/modules';
import { TotalBalanceCard } from '@peanuds/total-balance';

const uiStateStore = useUiStateStore();
const { dashboardInsightsExpanded } = storeToRefs(uiStateStore);
const isTotalBalanceCardEnabled = useFeatureFlag(features.dashboardTotalBalanceCard);
</script>

<template>
  <UiPageShell class="dashboard-page" data-testid="dashboard-root">
    <div class="dashboard">
      <div v-if="isTotalBalanceCardEnabled" class="dashboard__welcome-back">
        <TotalBalanceCard />
      </div>

      <div class="dashboard__main">
        <div
          :class="{
            dashboard__collapsible: true,
            'dashboard__collapsible--expanded': dashboardInsightsExpanded,
          }"
        >
          <IbanSwiper v-model="dashboardInsightsExpanded" />
        </div>

        <div class="dashboard__controls">
          <div class="dashboard__control-spending">
            <ControlSpendings />
          </div>

          <div class="dashboard__quick-transfer">
            <QuickTransfer />
          </div>
        </div>
      </div>
    </div>
  </UiPageShell>
</template>

<style lang="scss" scoped>
.dashboard-page {
  gap: $space-md;
}

.dashboard {
  display: flex;
  width: 100%;
  flex: 1;
  min-height: 0;

  &__welcome-back {
    height: auto;
    flex-shrink: 0;
    width: f(250, 320); // 15.625rem → 20rem (250px → 320px)
    margin-right: f(18, 24); // 1.5rem → 2.5rem (24px → 40px)

    @media screen and (max-width: $screen_normal) {
      width: 175px;
    }
  }

  &__main {
    display: flex;
    flex: 1;
    min-height: 0;
    min-width: 0;
    width: 100%;
    flex-direction: column;
    gap: f(18, 24); // 1.125rem → 1.5rem (18px → 24px)
  }

  &__collapsible {
    position: relative;
    width: 100%;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    gap: $static-space-2; // 0.5rem (8px)
    border-radius: var(--p-border-radius-xl);
    border: 1px solid var(--p-form-field-border-color);
    background: #fff;
    transition: height 0.3s ease;
    height: 3.125rem; // 50px - static value from design

    &--expanded {
      height: 192px;
    }
  }

  &__collapsible-content {
    flex: 1;
    min-width: 0;
  }

  &__collapsible-header {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    width: 40px; // static value
    gap: $static-space-3; // 0.75rem (12px)

    &--expanded {
      width: auto;
      min-width: 5.3125rem; // 85px - static value
      padding: $static-space-3 0; // 0.75rem 0 (12px 0)
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__iban-swiper {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__iban-controls {
    display: flex;
    justify-content: center;
    gap: $static-space-2; // 0.5rem (8px)
  }

  &__controls {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    gap: f(16, 24); // 1rem → 1.5rem (16px → 24px)
  }

  &__control-spending {
    display: flex;
    min-height: 0;
    min-width: 0;
    flex: 1;
  }

  &__quick-transfer {
    width: 15.625rem; // 250px - static value
  }
}
</style>
